import { LabelMode } from "./../../../../../utils/label-mode.enum";
import { Component, OnInit, OnDestroy, inject, signal } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Subscription, Observable, lastValueFrom } from "rxjs";
import { Router } from "@angular/router";
import { AuthService } from "../../services/auth.service";
import { ConfirmPasswordValidator } from "./confirm-password.validator";
import { UserModel } from "../../models/user.model";
import { debounceTime, distinctUntilChanged, first } from "rxjs/operators";
import { AuthState } from "../../models/auth.model";
import { SetError } from "src/utils/set-error.validator";
import { MatDialogRef } from "@angular/material/dialog";
import { LoginDialogComponent } from "../dialogs/login-dialog/login-dialog.component";
import { AddressService } from "src/app/services/api/address.service";
import { pick, pickWithout } from "src/utils/helper/object.helper";

@Component({
  selector: "app-registration",
  templateUrl: "./registration.component.html",
  styleUrls: ["./registration.component.scss"],
  standalone: false,
})
export class RegistrationComponent implements OnInit, OnDestroy {
  #addressService = inject(AddressService);



  registrationForm: FormGroup;
  hasError: boolean;
  isLoading$: Observable<boolean>;
  /** Briefly true right after a role switch, so every field below can fade as
   *  one instead of only the one field the role actually changes. */
  roleSwitching = signal(false);

  AuthStates = AuthState;

  LabelMode = LabelMode;
  error: any = {};

  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {
    this.isLoading$ = this.authService.isLoading$;
    // redirect to home if already logged in
    if (this.authService.currentUserValue) {
      this.router.navigate(["/"]);
    }
  }

  ngOnInit(): void {
    this.initForm();
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.registrationForm.controls;
  }

  initForm() {
    this.registrationForm = this.fb.group(
      {
        image: [null],
        image_url: [null],
        first_name: [null, Validators.required],
        last_name: [null, Validators.required],
        // identification: [null, [Validators.required, ValidateThaiNationalID]],
        email: [null, [Validators.required, Validators.email]],
        phone: [null, Validators.required],
        birthdate: [null, Validators.required],
        academy: [null],
        nickname: [null],
        password: [null, Validators.required],
        password_confirmation: [null, Validators.required],
        role_id: [1],
        // Consent to the privacy policy, so it must be an explicit click, not a
        // default the form already arrived with. `requiredTrue` rather than
        // `required` — `required` treats a checkbox's `false` as a present,
        // valid value, so an unchecked box never failed validation.
        agree: [false, Validators.requiredTrue],
        is_student: [true],

        // Address. These controls are also bound by <app-form>, which attaches
        // its own `Validators.required` to them — so the requiredness lives
        // there, not here, and removing it from this list has no effect.
        id: [null],
        name: [null, Validators.required],
        address: [null, Validators.required],
        province_id: [null, Validators.required],
        district_id: [null, Validators.required],
        sub_district_id: [null, Validators.required],
        post_code: [null, Validators.required],
      },
      {
        validator: ConfirmPasswordValidator.MatchPassword,
      }

    );
    this.initSubscription()
  }
  initSubscription() {
    this.registrationForm
      .get("province_id")
      ?.valueChanges.pipe(debounceTime(100), distinctUntilChanged())
      .subscribe((val) => {
        this.registrationForm.patchValue({ district_id: null });
      });
    this.registrationForm
      .get("district_id")
      ?.valueChanges.pipe(debounceTime(100), distinctUntilChanged())
      .subscribe((val) => {
        if (!val) {
          this.registrationForm.patchValue({ sub_district_id: null });
        }
      });
    this.registrationForm
      .get("sub_district_id")
      ?.valueChanges.pipe(debounceTime(100), distinctUntilChanged())
      .subscribe(async (val) => {
        if (val) {
          const sub_dist = await lastValueFrom(
            this.#addressService.getSubDistrict(val),
          );
          this.registrationForm.patchValue({ post_code: sub_dist.post_code });
        } else {
          this.registrationForm.patchValue({ post_code: null });
        }
      });
  }


  switchRole(value: boolean) {
    if (this.registrationForm.value.is_student === value) return;
    this.registrationForm.patchValue({ is_student: value });

    // Held for one timer rather than played once per click: a second click
    // inside the fade window just extends the hold instead of racing a fresh
    // animation against the one still running.
    this.roleSwitching.set(true);
    clearTimeout(this.#roleSwitchTimer);
    this.#roleSwitchTimer = setTimeout(() => this.roleSwitching.set(false), 350);
  }
  #roleSwitchTimer: ReturnType<typeof setTimeout> | undefined;

  async submit() {
    this.hasError = false;
    this.error = {};
    if (this.registrationForm.invalid) {
      // Pressing the button counts as visiting every field.
      //
      // The inline validation is gated on `touched`, so a form submitted
      // without being typed into showed nothing at all: the dialog just did
      // not close, with no red border and no message to say which of the
      // fourteen fields was the problem.
      this.registrationForm.markAllAsTouched();
      this.error = SetError(this.registrationForm);
      return;
    }
    const payload = this.setPayload('user');


    try {
      const registrationSubscr = await lastValueFrom(this.authService.registration(payload).pipe(first()))
      if (registrationSubscr) {
        // Only send an address when one was actually collected. An empty
        // address is rejected server-side, which would turn a successful
        // sign-up into a visible error after the account already exists.
        if (this.registrationForm.value.sub_district_id) {
          const addressPayload = { ...this.setPayload('address'), user_id: registrationSubscr.id }
          await lastValueFrom(this.#addressService.createAddress(addressPayload).pipe())
        }
        window.location.reload();
        this.unsubscribe.push(registrationSubscr);
      } else {
        // window.location.reload();
        // this.unsubscribe.push(registrationSubscr);
      }


    } catch (error) {
      this.hasError = true;
    }




  }
  selectImage(): void {
    document.getElementById("image-input")?.click();
  }

  updateAuthState(state: AuthState) {
    this.authService.updateAuthState(state);
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
    clearTimeout(this.#roleSwitchTimer);
  }


  setPayload(type: 'user' | 'address') {
    const rawValue = this.registrationForm.getRawValue()
    let payload = rawValue
    switch (type) {
      case 'user':
        payload = pickWithout(rawValue, ['id', 'name', 'address', 'province_id', 'district_id', 'sub_district_id', 'post_code'])
        break;

      case 'address':
        payload = pick(rawValue, ['id', 'name', 'address', 'province_id', 'district_id', 'sub_district_id', 'post_code'])
        break;
    }
    return payload
  }

}
